import type { EligibleProduct } from "../types";

/**
 * RDESQ-1988 — Clark Smart Offer Pilot cohort content overrides.
 *
 * When the `Sales-isUsingMovedDiscoveryAndEngagementQuestions` flag is on,
 * swap the en-US `phrase` (and Discovery `examples`) on matched checklist
 * items for the SKUs in `OVERRIDE_SKUS`. Spanish (es-US) copy is intentionally
 * left untouched.
 *
 * Engagement removal is handled separately in `usePushDiscoveryEngagementToTimeline`
 * (drop the engagement filter clause). The legacy checklist UI already hides
 * discovery + engagement sections when the flag is on.
 *
 * MCSFTJOYFUL and HPWHOLEHOME additionally carry `richPhrase` + `subfooter` (en-US only, like
 * everything here); 3002 and HOMEPRO are deliberately `phrase`-only. Where a `richPhrase` exists the
 * `phrase` beside it is kept on purpose: `ChecklistItem` renders `richPhrase` instead of it, but `phrase` stays the
 * plaintext source for the timeline and Honeycomb, so the two must say the same thing. Edit them
 * together.
 *
 * Temporary: this lives in code for speed/clarity during the pilot. Once the
 * pilot graduates, fold the new copy into Contentful and delete this module.
 */

const OVERRIDE_LOCALE = "en-US";

type ItemOverride = {
    phrase?: string;
    subheading?: string;
    /**
     * Markdown variant of `phrase` (see expandRichPhrase). ChecklistItem prefers it over `phrase`, so
     * an override blanking `phrase` to drop a bullet must blank this too — see SHARED_OVERRIDES.COVERAGE,
     * which does.
     */
    richPhrase?: string;
    subfooter?: string;
    examples?: string[];
};

const SHARED_OVERRIDES: Record<string, ItemOverride> = {
    DISCOVERY_QUESTION: {
        phrase: "Ask no more than two discovery questions during the Solve",
        examples: [
            "“What other devices around your home are connected or used with your phone?”",
            "“How many wi-fi connected devices do you have in your home such as TVs or laptops?”",
            "“If one of those main devices stopped working, how disruptive would that be for you or your family?”",
            "“Do you currently have protection on any of your other home electronics?”",
            "“Are your devices mostly helping with work, staying connected, entertainment, or a little of everything?”",
        ],
    },
    NON_PARTNER_DEVICES: {
        phrase: "“This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased.”",
    },
    FUTURE_DEVICES: {
        phrase: "“The plan covers products you currently own and future purchases of eligible devices, no receipts or registration required.”",
    },
    PROTECTION: {
        phrase: "“If you do file a claim, a service fee of $0 to no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.”",
    },
    // Rachel Bruno-Whitten (8/6) — remove the scripted "$25 plus tax per month…"
    // line from Core Benefits. Blanking the phrase drops the bullet (ChecklistItem
    // renders nothing for an empty phrase) while the COVERAGE item itself stays, so
    // its device-personalization carousel still renders. Applies to both HOMEPRO
    // (AT&T) and 3002 (Verizon) — identical shared wording.
    COVERAGE: {
        // Both blanked, not just `phrase`. The merge only assigns fields that are !== undefined, and
        // ChecklistItem prefers richPhrase — so omitting it here would let the first COVERAGE entry
        // that gains a richPhrase in Contentful silently resurrect the bullet this override removes.
        phrase: "",
        richPhrase: "",
    },
};

const OVERRIDES_BY_SKU: Record<string, Record<string, ItemOverride>> = {
    MCSFTJOYFUL: {
        // Standalone, not spread from SHARED_OVERRIDES: MCSFTJOYFUL carries its own Core Benefits
        // script and has no COVERAGE item, so none of the Verizon/ATT shared copy applies. Quotation
        // marks sit OUTSIDE the ==...== token throughout, so the emphasis colours the words an expert
        // must say and not the punctuation.
        //
        // The subheading names the highlight colour, so it is coupled to mark.verbatim in
        // @expert/theme/global.css — the two must move together, and a test pins the pairing.
        TRANSITION: {
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“I’m glad we resolved the issue. As I mentioned earlier, the ==Asurion Care Complete is a separate protection plan,== and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”\n\n- “Just like the ____ that you mentioned”",
            subfooter: "Go right from your Transition into the Core Benefits without hesitation.",
        },
        // Core Benefits: the pilot script's three sentences are split back onto the three items that
        // already exist in this SKU's "Core benefits" section, rather than consolidated onto
        // PROTECTION, which would have left NON_PARTNER_DEVICES and FUTURE_DEVICES repeating
        // themselves above it.
        NON_PARTNER_DEVICES: {
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“==This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased.==” (Relevant Conversational Language)",
        },
        FUTURE_DEVICES: {
            phrase: "required",
            richPhrase:
                "“==The plan covers products you currently own and future purchases of eligible devices, no receipts or registration required.==” (Relevant Conversational Language)",
        },
        PROTECTION: {
            subheading:
                "Incorporate conversational language, reference back to devices or customer lifestyle that was uncovered during discovery to personalize the offer.",
            phrase: "required",
            richPhrase:
                "“==If you do file a claim, a service fee of $0 to no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.==” (Relevant Conversational Language)\n\n“==The plan is $24.99 plus tax per month and again covers all eligible devices in your home.==” (Relevant Conversational Language)",
        },
        ASK_SALE: {
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“We can go ahead and get this set up for you today and get your home tech covered.”\n\n“==May I start your enrollment?==”",
        },
    },
    // HPWHOLEHOME (HP Care Complete Whole Home) — same treatment as MCSFTJOYFUL. Only TRANSITION and
    // COVERAGE are reachable by key: the rest of this SKU's checklist items are keyed "UNSET" in
    // Contentful (five of them, so the key is not even unique), so they are targeted by phrase in
    // UNKEYED_OVERRIDES_BY_SKU.
    HPWHOLEHOME: {
        TRANSITION: {
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“I’m glad we resolved the issue. As I mentioned earlier, the ==HP Care Complete Whole Home program is a separate protection plan,== and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”\n\n- “Just like the __ you mentioned!”",
            subfooter: "Go right from your Transition into the Core Benefits without hesitation.",
        },
        // Borrowed rather than spread: this is the only shared entry that applies to this SKU. Unlike
        // 3002/HOMEPRO, where the blanked line was a scripted "$25 plus tax per month", HP's COVERAGE
        // reads "Personalize the coverage" — an instruction to the expert, and one PROTECTION's
        // subheading already gives. Blanking drops the bullet and keeps the item, so the
        // device-personalization carousel ChecklistSection renders from it survives.
        COVERAGE: SHARED_OVERRIDES.COVERAGE,
    },
    "3002": {
        ...SHARED_OVERRIDES,
        TRANSITION: {
            phrase: "“I’m glad we resolved the issue. As I mentioned earlier, the Verizon Home Device Protect program is separate from your mobile device insurance, and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”",
        },
    },
    HOMEPRO: {
        ...SHARED_OVERRIDES,
        // ATT "Advanced Discovery" (7/27 Cebu ramp) — 5 device-connectivity discovery
        // questions, one per call reason. Shown as an examples carousel; the agent picks
        // the one matching the call reason (no system auto-select). Overrides the shared
        // DISCOVERY_QUESTION examples while keeping the shared phrase. HOMEPRO (ATT) only —
        // Verizon SKU 3002 keeps SHARED_OVERRIDES. en-US only.
        DISCOVERY_QUESTION: {
            ...SHARED_OVERRIDES.DISCOVERY_QUESTION,
            examples: [
                "Phone Activation — “While we’re getting your new phone activated, I want to make sure everything you depend on reconnects correctly. What other devices do you normally connect your phone with — watches, tablets, laptops, headphones, smart TVs, or anything else?”",
                "Connectivity / Network Issues — “While I’m troubleshooting the data connection, are you only having trouble on this phone, or are other connected devices like a computer, iPad, smart TV, or gaming console also impacted?”",
                "Billing / Payment Calls — “As I look through the charges, I want to make sure everything matches how you’re using your service. Have you recently added or connected any new technology, like a smartwatch, tablet, laptop, hotspot device?”",
                "Cannot Make / Receive Calls — “For me to understand the full impact, are the other devices on the network in your home working correctly such as laptop, iPad, smartwatch?”",
                "Messaging Issues — “Do you only use messaging on your phone, or do you connect it with another device like a tablet, smartwatch, or computer?”",
            ],
        },
        TRANSITION: {
            phrase: "“I’m glad we resolved the issue. As I mentioned earlier, the AT&T HomeTech Protection program is separate from your mobile device insurance, and you’re not currently enrolled. We highly recommend this coverage for customers who regularly use home tech.”",
        },
    },
};

/**
 * Overrides for items that cannot be reached by key.
 *
 * HPWHOLEHOME's Contentful checklist leaves five of its items keyed `UNSET` — the three Core Benefits
 * bullets and both Enrollment transparency bullets — where HOMEPRO, 3002 and MCSFTJOYFUL all carry the
 * real keys (NON_PARTNER_DEVICES / FUTURE_DEVICES / PROTECTION, BILLING / ASK_SALE). A key that is both
 * absent and duplicated five times cannot address a single item, so these match on a distinctive
 * substring of the item's existing `phrase` instead. `matchedBy` names the key the item SHOULD carry.
 *
 * This is a workaround for missing content, not a second content model: setting those five keys in
 * Contentful makes every entry here redundant, and it should be deleted when that happens. Matching is
 * fail-safe — no match leaves the item exactly as Contentful authored it — and key matching always
 * wins, so a SKU that gains real keys stops consulting this list for those items.
 */
type UnkeyedOverride = ItemOverride & {
    /** Case-sensitive substring of the item's Contentful `phrase`. First match wins. */
    matchPhrase: string;
    /** The key this item should have; documentation only, never compared. */
    matchedBy: string;
};

const UNKEYED_OVERRIDES_BY_SKU: Record<string, UnkeyedOverride[]> = {
    HPWHOLEHOME: [
        // Core Benefits, split across the three bullets that already exist in this SKU's section rather
        // than consolidated onto one — the same split MCSFTJOYFUL uses, and now the same copy: these three
        // read identically to that SKU's, since the script is the shared Core Benefits wording and only
        // TRANSITION names the plan. Kept written out per SKU rather than shared, so a later divergence
        // edits one SKU without reasoning about the other.
        {
            matchedBy: "NON_PARTNER_DEVICES",
            matchPhrase: "covers breakdowns on all eligible products",
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“==This plan covers breakdowns on all eligible products for one monthly price no matter when or where they were purchased.==” (Relevant Conversational Language)",
        },
        {
            matchedBy: "FUTURE_DEVICES",
            matchPhrase: "products you currently own are covered",
            phrase: "required",
            richPhrase:
                "“==The plan covers products you currently own and future purchases of eligible devices, no receipts or registration required.==” (Relevant Conversational Language)",
        },
        {
            matchedBy: "PROTECTION",
            matchPhrase: "service fee of no more than $99",
            subheading:
                "Incorporate conversational language, reference back to devices or customer lifestyle that was uncovered during discovery to personalize the offer.",
            phrase: "required",
            richPhrase:
                "“==If you do file a claim, a service fee of $0 to no more than $99 plus tax is required per claim, and your home tech will be repaired, replaced, or reimbursed.==” (Relevant Conversational Language)\n\n“==The plan is $24.99 plus tax per month and again covers all eligible devices in your home.==” (Relevant Conversational Language)",
        },
        {
            matchedBy: "ASK_SALE",
            matchPhrase: "May I start your enrollment?",
            subheading: "Say these exact words (purple highlight)",
            phrase: "required",
            richPhrase:
                "“We can go ahead and get this set up for you today and get your home tech covered.”\n\n“==May I start your enrollment?==”",
        },
    ],
};

/**
 * Overrides for copy that is NOT a checklist item, so it cannot be keyed into OVERRIDES_BY_SKU —
 * the apply loop below only visits real `checklistItems`, so a made-up key there would silently
 * never apply. `sendLinkCopy` is read by getSendLinkCopy; `saleSuccessMessage` replaces the
 * product-level Contentful field of the same name.
 */
type ProductOverride = {
    saleSuccessMessage?: string;
    sendLinkCopy?: string;
};

const PRODUCT_OVERRIDES_BY_SKU: Record<string, ProductOverride> = {
    MCSFTJOYFUL: {
        saleSuccessMessage:
            "“==Congratulations on signing up for Asurion Care Complete, be on the lookout for a welcome email within the next few days with complete details about all the amazing benefits!==”",
        sendLinkCopy:
            "“Great, ==can I text you a link to review important plan information and complete your enrollment?==”",
    },
    // No saleSuccessMessage: the HPWHOLEHOME slide supplies no congratulations line, so this SKU
    // keeps whatever Contentful has rather than inheriting Microsoft's wording with the plan name
    // swapped.
    HPWHOLEHOME: {
        sendLinkCopy:
            "“Great, ==can I text you a link to review important plan information and complete your enrollment?==”",
    },
};

/**
 * The pilot's send-link ask, for `getSendLinkCopy` to prefer over its hardcoded default. Returns
 * undefined when the flag is off or the SKU has no override, so callers keep their existing copy.
 */
export function getCohortSendLinkCopy(productSku: string, isCohortFlagOn: boolean): string | undefined {
    if (!isCohortFlagOn) return undefined;
    return PRODUCT_OVERRIDES_BY_SKU[productSku]?.sendLinkCopy;
}

export function applyCohortContentOverrides(products: EligibleProduct[], isCohortFlagOn: boolean): EligibleProduct[] {
    if (!isCohortFlagOn) return products;

    return products.map((product) => {
        const overrides = OVERRIDES_BY_SKU[product.productSku];
        const unkeyedOverrides = UNKEYED_OVERRIDES_BY_SKU[product.productSku];
        const productOverride = PRODUCT_OVERRIDES_BY_SKU[product.productSku];
        if (!overrides && !unkeyedOverrides && !productOverride) return product;

        const localizedInfo = product.productInfo?.[OVERRIDE_LOCALE];
        if (!localizedInfo) return product;

        // Product-level fields are applied first and unconditionally: a SKU can legitimately have a
        // PRODUCT_OVERRIDES_BY_SKU entry and no checklist, and the guard below would otherwise discard
        // it — the same SKU the `!overrides && !productOverride` check above deliberately let through.
        const withProductOverride = {
            ...localizedInfo,
            ...(productOverride?.saleSuccessMessage !== undefined && {
                saleSuccessMessage: productOverride.saleSuccessMessage,
            }),
        };

        if (!localizedInfo.salesOfferChecklist) {
            return {
                ...product,
                productInfo: { ...product.productInfo, [OVERRIDE_LOCALE]: withProductOverride },
            };
        }

        const updatedSections = localizedInfo.salesOfferChecklist.checklistSections.map((section) => ({
            ...section,
            checklistItems: section.checklistItems.map((item) => {
                // Key first: an item with a real key never consults the phrase list.
                const override =
                    overrides?.[item.key] ??
                    unkeyedOverrides?.find((candidate) => item.phrase?.includes(candidate.matchPhrase));
                if (!override) return item;
                return {
                    ...item,
                    ...(override.phrase !== undefined && { phrase: override.phrase }),
                    ...(override.subheading !== undefined && { subheading: override.subheading }),
                    ...(override.richPhrase !== undefined && { richPhrase: override.richPhrase }),
                    ...(override.subfooter !== undefined && { subfooter: override.subfooter }),
                    ...(override.examples !== undefined && { examples: override.examples }),
                };
            }),
        }));

        return {
            ...product,
            productInfo: {
                ...product.productInfo,
                [OVERRIDE_LOCALE]: {
                    ...withProductOverride,
                    salesOfferChecklist: {
                        ...localizedInfo.salesOfferChecklist,
                        checklistSections: updatedSections,
                    },
                },
            },
        };
    });
}
